import type { MobileBlurType, ProtectionState } from '../types';

/**
 * CSS class names for blur overlay elements
 */
const CLASSES = {
    container: 'copydeter-container',
    blurTL: 'copydeter-blur-tl',
    blurBR: 'copydeter-blur-br',
    blurActive: 'copydeter-blur-active'
} as const;

/** Marks a half whose blur comes from backdrop-filter (a container overlay) rather than filter. */
const BACKDROP_HALF_ATTR = 'data-copydeter-backdrop-half';

/**
 * Clip-path pairs for each blur type.
 * clipPathA = first half (initially clear), clipPathB = second half (initially blurred).
 * To add a new type, add an entry here -- no other changes required.
 */
const BLUR_PATTERNS: Record<MobileBlurType, { clipPathA: string; clipPathB: string }> = {
    diagonal: {
        clipPathA: 'polygon(0 0, 100% 0, 0 100%)', // top-left triangle
        clipPathB: 'polygon(100% 0, 100% 100%, 0 100%)' // bottom-right triangle
    },
    horizontal: {
        clipPathA: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)', // top half
        clipPathB: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)' // bottom half
    }
};

/**
 * Creates diagonal blur overlays for a protected element
 * Uses cloned images with filter:blur instead of backdrop-filter for better browser compatibility
 */
export function createBlurOverlay(
    element: Element,
    blurType: MobileBlurType = 'diagonal'
): Partial<ProtectionState> {
    const htmlElement = element as HTMLElement;
    const isImg = element.tagName === 'IMG';
    const pattern = BLUR_PATTERNS[blurType];

    // Non-<img> targets have no image to clone; blur them with a backdrop-filter overlay.
    if (!isImg) {
        return createBackdropOverlay(htmlElement, pattern);
    }

    const computed = window.getComputedStyle(htmlElement);
    const isPositioned = computed.position === 'absolute' || computed.position === 'fixed';
    const hasTransform = computed.transform && computed.transform !== 'none';

    // Elements that are absolutely/fixed positioned AND have a CSS transform
    // (e.g. centered with translate(-50%,-50%)) need a different strategy: the
    // container fills the positioned ancestor and clones inherit their positioning
    // from the CSS cascade.  Elements that are just absolutely positioned without
    // a transform (e.g. width:100% height:100% fills) work fine with the normal
    // wrapping approach.
    if (isPositioned && hasTransform) {
        return createOverlayForAbsoluteElement(htmlElement, isImg, pattern);
    }

    return createOverlayForFlowElement(htmlElement, isImg, pattern);
}

/**
 * Overlay strategy for elements in normal document flow (grid thumbnails, etc.).
 * Inserts clones as siblings of the element rather than wrapping it in a
 * container.  This is critical for React-managed elements: wrapping moves the
 * node in the DOM which breaks React's reconciliation (removeChild errors when
 * virtualised items scroll out of view).
 */
function createOverlayForFlowElement(
    htmlElement: HTMLElement,
    isImg: boolean,
    pattern: { clipPathA: string; clipPathB: string }
): Partial<ProtectionState> {
    if (!htmlElement.parentNode) {
        return { blurSwapped: false };
    }

    const blurOverlayTL = createBlurredClone(
        htmlElement,
        isImg,
        'tl',
        false,
        false,
        pattern.clipPathA
    );
    const blurOverlayBR = createBlurredClone(
        htmlElement,
        isImg,
        'br',
        true,
        false,
        pattern.clipPathB
    );

    htmlElement.after(blurOverlayTL, blurOverlayBR);

    return { blurOverlayTL, blurOverlayBR, blurSwapped: false, siblingMode: true };
}

/**
 * Overlay strategy for absolutely/fixed-positioned elements.
 * The container fills the positioned ancestor (inset 0) so that the original
 * element and clones all share the same coordinate space.  Clone sizing and
 * positioning is left to the CSS cascade (they match the same selectors as the
 * original element because they are still descendant <img>s of the same parent).
 */
function createOverlayForAbsoluteElement(
    htmlElement: HTMLElement,
    isImg: boolean,
    pattern: { clipPathA: string; clipPathB: string }
): Partial<ProtectionState> {
    const container = document.createElement('div');
    container.className = CLASSES.container;

    Object.assign(container.style, {
        position: 'absolute',
        top: '0',
        right: '0',
        bottom: '0',
        left: '0',
        overflow: 'hidden',
        // Override inherited pointer-events:none from .photo-detail-view-photo so our
        // touch handlers can fire.  Events still bubble up to the parent filmstrip container.
        pointerEvents: 'auto'
    });

    const parent = htmlElement.parentNode;

    if (!parent) {
        return { blurSwapped: false };
    }

    parent.insertBefore(container, htmlElement);
    container.appendChild(htmlElement);

    const blurOverlayTL = createBlurredClone(
        htmlElement,
        isImg,
        'tl',
        false,
        true,
        pattern.clipPathA
    );
    const blurOverlayBR = createBlurredClone(
        htmlElement,
        isImg,
        'br',
        true,
        true,
        pattern.clipPathB
    );

    container.appendChild(blurOverlayTL);
    container.appendChild(blurOverlayBR);

    return { container, blurOverlayTL, blurOverlayBR, blurSwapped: false };
}

/**
 * Overlay strategy for non-<img> targets, whose content can't be cloned. Injects two clip-path
 * halves that blur via backdrop-filter -- tracking whatever paints behind them -- as the
 * element's own children (the caller supplies a positioned mount point). Halves swap on
 * long-press like the cloned-image overlays; a short tap passes through, since backdrop mode
 * forwards no click.
 */
function createBackdropOverlay(
    htmlElement: HTMLElement,
    pattern: { clipPathA: string; clipPathB: string }
): Partial<ProtectionState> {
    // Establish a containing block for the absolutely-positioned halves.
    if (window.getComputedStyle(htmlElement).position === 'static') {
        htmlElement.style.position = 'relative';
    }

    const blurOverlayTL = createBackdropHalf('tl', false, pattern.clipPathA);
    const blurOverlayBR = createBackdropHalf('br', true, pattern.clipPathB);

    htmlElement.appendChild(blurOverlayTL);
    htmlElement.appendChild(blurOverlayBR);

    return { blurOverlayTL, blurOverlayBR, blurSwapped: false, backdropMode: true };
}

/**
 * Creates one clip-path half of a backdrop-filter overlay. The blurred half applies
 * backdrop-filter: blur() (with the -webkit- prefix for iOS Safari); the clear half passes
 * through. Pointer-events are disabled so taps reach the content beneath. Marked with
 * BACKDROP_HALF_ATTR so setBlurActive knows to toggle backdrop-filter (not filter) on swap.
 */
function createBackdropHalf(
    position: 'tl' | 'br',
    isBlurred: boolean,
    clipPath: string
): HTMLElement {
    const half = document.createElement('div');

    half.className = position === 'tl' ? CLASSES.blurTL : CLASSES.blurBR;
    half.setAttribute('data-copydeter-clone', '');
    half.setAttribute(BACKDROP_HALF_ATTR, '');

    if (isBlurred) {
        half.classList.add(CLASSES.blurActive);
    }

    const backdrop = isBlurred ? 'blur(8px)' : 'none';

    Object.assign(half.style, {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        clipPath,
        WebkitClipPath: clipPath,
        pointerEvents: 'none',
        // iOS Safari only composites (and blurs) a backdrop-filter layer when the element
        // paints something, so give each half a near-invisible background.
        backgroundColor: 'rgba(0, 0, 0, 0.001)',
        backdropFilter: backdrop,
        WebkitBackdropFilter: backdrop,
        transition: 'backdrop-filter 0.15s ease, -webkit-backdrop-filter 0.15s ease'
    });

    return half;
}

/**
 * Creates a cloned element with blur filter and clip-path.
 *
 * @param inheritCssPositioning When true the clone receives only clip-path and
 *   filter styles; all positioning / sizing is left to the CSS cascade so the
 *   clone lands exactly where the original element does (used for
 *   absolutely-positioned elements whose layout is controlled by stylesheet
 *   rules like `.photo-image img`).
 */
function createBlurredClone(
    original: HTMLElement,
    isImg: boolean,
    position: 'tl' | 'br',
    isBlurred: boolean,
    inheritCssPositioning: boolean,
    clipPath: string
): HTMLElement {
    let clone: HTMLElement;

    if (isImg) {
        const imgClone = document.createElement('img');
        imgClone.src = (original as HTMLImageElement).src;
        imgClone.alt = '';
        imgClone.draggable = false;
        clone = imgClone;
    } else {
        clone = document.createElement('div');
    }

    clone.className = position === 'tl' ? CLASSES.blurTL : CLASSES.blurBR;
    clone.setAttribute('data-copydeter-clone', '');

    if (isBlurred) {
        clone.classList.add(CLASSES.blurActive);
    }

    if (inheritCssPositioning) {
        // Only overlay-specific styles; positioning comes from CSS cascade
        Object.assign(clone.style, {
            clipPath,
            WebkitClipPath: clipPath,
            pointerEvents: 'none',
            filter: isBlurred ? 'blur(8px)' : 'none',
            transition: 'filter 0.15s ease'
        });
    } else {
        // Explicit positioning to fill the wrapping container.
        Object.assign(clone.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            objectFit: (original as HTMLImageElement).style.objectFit || 'cover',
            clipPath,
            WebkitClipPath: clipPath,
            pointerEvents: 'none',
            filter: isBlurred ? 'blur(8px)' : 'none',
            transition: 'filter 0.15s ease'
        });
    }

    return clone;
}

/**
 * Swaps which diagonal half is blurred
 */
export function swapBlur(state: ProtectionState, debug = false): void {
    const { blurOverlayTL, blurOverlayBR, blurSwapped } = state;

    if (debug) {
        console.log('[CopyDeter] swapBlur called', {
            blurSwapped,
            hasTL: !!blurOverlayTL,
            hasBR: !!blurOverlayBR
        });
    }

    if (!blurOverlayTL || !blurOverlayBR) {
        return;
    }

    if (blurSwapped) {
        // Swap back to initial state: TL clear, BR blurred
        setBlurActive(blurOverlayTL, false);
        setBlurActive(blurOverlayBR, true);
        state.blurSwapped = false;
    } else {
        // Swap: TL blurred, BR clear
        setBlurActive(blurOverlayTL, true);
        setBlurActive(blurOverlayBR, false);
        state.blurSwapped = true;
    }

    if (debug) {
        console.log('[CopyDeter] swapBlur complete', { newBlurSwapped: state.blurSwapped });
    }
}

/**
 * Sets or removes blur on an overlay element. Cloned <img> halves blur themselves via `filter`;
 * backdrop overlay halves blur whatever paints behind them via `backdrop-filter` -- so the swap
 * has to toggle whichever property that half actually uses.
 */
function setBlurActive(element: HTMLElement, isBlurred: boolean): void {
    const value = isBlurred ? 'blur(8px)' : 'none';

    if (element.hasAttribute(BACKDROP_HALF_ATTR)) {
        element.style.backdropFilter = value;
        element.style.setProperty('-webkit-backdrop-filter', value);
    } else {
        element.style.filter = value;
    }

    if (isBlurred) {
        element.classList.add(CLASSES.blurActive);
    } else {
        element.classList.remove(CLASSES.blurActive);
    }
}

/**
 * Removes blur overlays and unwraps the element (when wrapped)
 */
export function removeBlurOverlay(state: ProtectionState): void {
    const { element, container, blurOverlayTL, blurOverlayBR } = state;

    blurOverlayTL?.remove();
    blurOverlayBR?.remove();

    if (!state.siblingMode && container && container.parentNode) {
        container.parentNode.insertBefore(element, container);
        container.remove();
    }
}
